import type {VariationPart} from "#template/variations/configuration"

export type TreeNodeRow = {kind: "node"; key: string; label: string; tooltip: string; countLabel: string; expanded: boolean; current: boolean; indent: number}
export type TreeCaptionRow = {kind: "caption"; key: string; label: string; indent: number}

export type GroupRanking = {
    rankOf: (key: string) => number
    optionIndexOf: (key: string, variantId: string) => number
}

const leafParts = 2

export function compareParts(a: readonly VariationPart[], b: readonly VariationPart[], ranking: GroupRanking) {
    for (let index = 0; index < Math.min(a.length, b.length); index++) {
        const rankA = ranking.rankOf(a[index].key)
        const rankB = ranking.rankOf(b[index].key)
        if (rankA !== rankB) return rankA - rankB
        const optionA = ranking.optionIndexOf(a[index].key, a[index].variantId)
        const optionB = ranking.optionIndexOf(b[index].key, b[index].variantId)
        if (optionA !== optionB) return optionA - optionB
    }
    return b.length - a.length
}

export function buildTree<Leaf>(
    count: number,
    partsOf: (index: number) => readonly VariationPart[],
    leaf: (index: number, parts: VariationPart[], indent: number) => Leaf,
    expanded: ReadonlySet<string>,
    configuration: Readonly<Record<string, unknown>>,
    measure: (index: number) => {done: number; total: number},
    ranking: GroupRanking,
): (TreeNodeRow | TreeCaptionRow | Leaf)[] {
    const firstAfter = (index: number, after: number) => {
        const parts = partsOf(index)
        let first = 0
        while (first < parts.length && ranking.rankOf(parts[first].key) <= after) first++
        return first
    }
    const nextGroup = (start: number, end: number, after: number) => {
        let next: VariationPart | undefined
        for (let index = start; index < end; index++) {
            const candidate = partsOf(index)[firstAfter(index, after)]
            if (candidate && (!next || ranking.rankOf(candidate.key) < ranking.rankOf(next.key))) next = candidate
        }
        return next
    }
    const valueOf = (index: number, after: number, group: VariationPart) => {
        const part = partsOf(index)[firstAfter(index, after)]
        return part?.key === group.key ? part : undefined
    }
    const sameValue = (a: VariationPart | undefined, b: VariationPart | undefined) => a?.variantId === b?.variantId
    const branches = (start: number, end: number, after: number) => {
        for (let index = start; index < end; index++) if (partsOf(index).length - firstAfter(index, after) > leafParts) return true
        return false
    }
    const rows: (TreeNodeRow | TreeCaptionRow | Leaf)[] = []

    const visit = (start: number, end: number, after: number, indent: number, path: string, onCurrentPath: boolean) => {
        let caption: string | undefined
        const pushLeaf = (index: number) => {
            const parts = partsOf(index).slice(firstAfter(index, after))
            const groups = parts.map((part) => part.group).join(" · ")
            if (groups !== caption) {
                caption = groups
                rows.push({kind: "caption", key: `${path}#${index}`, label: groups, indent})
            }
            rows.push(leaf(index, parts, indent))
        }
        const group = nextGroup(start, end, after)
        if (!group) {
            for (let index = start; index < end; index++) pushLeaf(index)
            return
        }
        const groupRank = ranking.rankOf(group.key)
        let index = start
        while (index < end) {
            const value = valueOf(index, after, group)
            let runEnd = index + 1
            while (runEnd < end && sameValue(valueOf(runEnd, after, group), value)) runEnd++
            if (!branches(index, runEnd, after)) {
                for (let other = index; other < runEnd; other++) pushLeaf(other)
                index = runEnd
                continue
            }
            caption = undefined
            const labels = [value ? `${group.group}: ${value.variant}` : `${group.group}: none`]
            const keys = [`${group.key}=${value?.variantId ?? ""}`]
            let current = onCurrentPath && configuration[group.key] === value?.variantId
            let nodeAfter = groupRank
            for (;;) {
                const next = nextGroup(index, runEnd, nodeAfter)
                if (!next) break
                const nextValue = valueOf(index, nodeAfter, next)
                let homogeneous = true
                for (let other = index + 1; other < runEnd && homogeneous; other++) homogeneous = sameValue(valueOf(other, nodeAfter, next), nextValue)
                if (!homogeneous || !nextValue || !branches(index, runEnd, nodeAfter)) break
                labels.push(`${next.group}: ${nextValue.variant}`)
                keys.push(`${next.key}=${nextValue.variantId}`)
                current &&= configuration[next.key] === nextValue.variantId
                nodeAfter = ranking.rankOf(next.key)
            }
            let done = 0
            let total = 0
            for (let other = index; other < runEnd; other++) {
                const measured = measure(other)
                done += measured.done
                total += measured.total
            }
            const key = `${path}/${keys.join("/")}`
            const isExpanded = expanded.has(key)
            rows.push({
                kind: "node",
                key,
                label: labels.join(" · "),
                tooltip: labels.join(", "),
                countLabel: `${done.toLocaleString()} / ${total.toLocaleString()}`,
                expanded: isExpanded,
                current,
                indent,
            })
            if (isExpanded) visit(index, runEnd, nodeAfter, indent + 1, key, current)
            index = runEnd
        }
    }
    visit(0, count, -Infinity, 0, "", true)
    return rows
}

export function toggled(set: ReadonlySet<string>, key: string) {
    const next = new Set(set)
    if (!next.delete(key)) next.add(key)
    return next
}
